import { ReactNodeViewRenderer, Node, mergeAttributes } from '@tiptap/react'
import { type CustomizerData } from '#app/utils/customizer.client.js'
import { Component } from './component'

declare module '@tiptap/core' {
	interface Commands<ReturnType> {
		linkButton: {
			setLinkButton: () => ReturnType
			setLinkButtonAlign: (align: string) => ReturnType
		}
	}
}

export type LinkButtonOptions = {
	/**
	 * Customizer data, including the theme.
	 * @default {}
	 * @example { data: { theme } }
	 */
	data: CustomizerData
}

export const LinkButton = Node.create<LinkButtonOptions>({
	name: 'linkButton',

	isolating: true,

	content: 'inline*',

	defining: true,

	group: 'block',

	draggable: true,

	selectable: true,

	addOptions() {
		return {
			data: {},
		}
	},

	addAttributes() {
		return {
			align: {
				default: 'left',
			},
			link: {
				default: null,
				parseHTML: element => element.getAttribute('href'),
				renderHTML: attributes => {
					let link
					switch (attributes.variable) {
						case 'referralLink':
							link = `https://{{sub.referralLink;0}}`
							break
						case 'verificationLink':
							link = `https://{{sub.verificationLink;0}}`
							break
						case 'subscriberLink':
							link = `https://{{sub.subscriberLink;0}}`
							break
						case 'projectLink':
							link = `https://{{list.projectLink;0}}`
							break
						default:
							link = attributes.link
					}

					return {
						href: link,
					}
				},
			},
			variable: {
				default: 'none',
				parseHTML: element => element.getAttribute('data-variable'),
				renderHTML: attributes => {
					if (!attributes.variable) {
						return {}
					}
					return {
						'data-variable': attributes.variable,
					}
				},
			},
		}
	},

	parseHTML() {
		return [
			{
				tag: 'a[data-type="linkButton"]',
			},
		]
	},

	renderHTML({ HTMLAttributes, node }) {
		let style = ''
		switch (node.attrs.align) {
			case 'center':
				style = 'margin-left: auto; margin-right: auto;'
				break
			case 'end':
				style = 'margin-left: auto; margin-right: 0;'
				break
			case 'start':
				style = 'margin-left: 0; margin-right: auto;'
				break
		}

		return [
			'a',
			mergeAttributes(HTMLAttributes, {
				'data-type': 'linkButton',
				class: 'button',
				style,
			}),
			0,
		]
	},

	addCommands() {
		return {
			setLinkButton:
				() =>
				({ commands }) =>
					commands.insertContent({
						type: 'linkButton',
						attrs: {
							theme: this.options.data.theme,
						},
						content: [{ type: 'text', text: 'Click here' }],
					}),
			setLinkButtonAlign:
				align =>
				({ commands }) =>
					commands.updateAttributes('linkButton', { align }),
		}
	},

	addNodeView() {
		return ReactNodeViewRenderer(Component)
	},
})

export default LinkButton
